import {
  Box,
  Button,
  Center,
  Divider,
  Skeleton,
  Stack,
  Title,
  useMantineColorScheme,
  useMatches,
} from "@mantine/core";
import { useLocalStorage } from "@mantine/hooks";
import { IconPlus } from "@tabler/icons-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import { DataGrid, PerformanceChart, PlaceholderValue, TextSerif } from "@/components/elements";
import { defaultCurrency } from "@/lib/currency";
import { usePortfolio } from "@/services/portfolio/get-portfolio";
import { usePortfolioPerformance } from "@/services/portfolio/get-portolio-performance";
import { formatCurrency } from "@/utils/formatEuro";
import { tooltipFormatter } from "@/utils/tooltipFormatter";

import type { LabelValueProps } from "@/components/elements";
import type { Currency } from "@/types/currency";
import type { DatesRange } from "@/types/entity";
import type { PerformanceChart as PerformanceChartData } from "@/types/performance";
import type { MantineStyleProps } from "@mantine/core";
import type { FC } from "react";

export type PortfolioPerformanceProps = MantineStyleProps & {
  performance?: PerformanceChartData;
  isEmptyState?: boolean;
  openCreateGarageModal?: () => void;
};

export const PortfolioPerformance: FC<PortfolioPerformanceProps> = ({
  performance,
  isEmptyState: isEmptyStateProp,
  openCreateGarageModal,
  ...props
}) => {
  const { t } = useTranslation();
  const [range, setRange] = useState<DatesRange>();
  const { data: portfolio, isLoading } = usePortfolio();
  const { data, isFetching, isRefetching } = usePortfolioPerformance(range);

  /* Theme */
  const { colorScheme } = useMantineColorScheme();
  const isDark = colorScheme === "dark";

  /* Media queries */
  const isMobile = useMatches({ base: true, sm: false });
  const fontSize = { base: "sm", sm: "sm" };
  const size = isMobile ? "sm" : "lg";

  const [currency] = useLocalStorage<Currency>({
    key: "currency",
    defaultValue: defaultCurrency,
  });

  const hasChartData =
    (performance?.data?.length ?? 0) > 0 || (data?.performance?.data?.length ?? 0) > 0;

  const dataGridItems: LabelValueProps[] = [
    {
      label: t("dashboard.portfolio.value"),
      value:
        portfolio?.value !== undefined && portfolio.value !== 0 ? (
          formatCurrency(portfolio.value, currency.code)
        ) : (
          <PlaceholderValue w={80} lines={1} />
        ),
      size: size,
      fz: fontSize,
      infoIcon: t("tooltips.portfolioValue"),
    },
    {
      label: t("dashboard.portfolio.garage"),
      value: (
        <Box data-testid="portfolio-garage-count">
          {portfolio?.num_garages !== undefined ? (
            portfolio.num_garages
          ) : (
            <PlaceholderValue w={80} lines={1} />
          )}
        </Box>
      ),
      size,
      fz: fontSize,
    },
    {
      label: t("dashboard.portfolio.cars"),
      value:
        portfolio?.num_cars !== undefined && portfolio.num_cars !== 0 ? (
          portfolio.num_cars
        ) : (
          <PlaceholderValue w={80} lines={1} />
        ),
      size: size,
      fz: fontSize,
    },
    {
      label: t("dashboard.portfolio.investments"),
      value:
        portfolio?.investment_capital !== undefined && portfolio.investment_capital !== 0 ? (
          formatCurrency(portfolio.investment_capital, currency.code)
        ) : (
          <PlaceholderValue w={80} lines={1} />
        ),
      size: size,
      fz: fontSize,
      infoIcon: t("tooltips.investedCapital"),
    },
  ];

  return (
    <Stack {...props}>
      {(!isMobile || !isEmptyStateProp) && (
        <>
          <Skeleton visible={isLoading}>
            <DataGrid title="" items={dataGridItems} />
          </Skeleton>
          <Divider />
        </>
      )}
      <Skeleton visible={isFetching}>
        <Stack pos="relative">
          <PerformanceChart
            performance={performance ?? data?.performance}
            benchmarks={[]}
            loading={isRefetching}
            onTimeframeChange={isEmptyStateProp ? undefined : setRange}
            tooltipFormatter={tooltipFormatter}
            restrictKpisByPlan={false}
            isEmptyState={isEmptyStateProp || !hasChartData}
          />

          {isEmptyStateProp && (
            <Stack
              mih={200}
              align="center"
              justify="center"
              pos="absolute"
              top={0}
              right={0}
              bottom={50}
              left={0}
              p="xl"
              style={{
                zIndex: 1,
                overflow: "hidden",
              }}
            >
              <Box
                pos="absolute"
                inset={0}
                style={{
                  background: isDark ? "#1919199d" : "#ffffff8d",
                  zIndex: 0,
                }}
              />

              <Title order={3} ta="center" style={{ zIndex: 1 }}>
                {t("dashboard.garage.create_title")}
              </Title>

              <TextSerif
                ta="center"
                style={{ zIndex: 1, textWrap: isMobile ? "balance" : undefined }}
                maw={isMobile ? 250 : undefined}
              >
                {t("dashboard.garage.empty_message")}
              </TextSerif>

              <Center style={{ zIndex: 1 }}>
                <Button
                  leftSection={<IconPlus />}
                  onClick={openCreateGarageModal}
                  aria-label={t("dashboard.garage.create_button")}
                >
                  {t("dashboard.garage.create_button")}
                </Button>
              </Center>
            </Stack>
          )}
        </Stack>
      </Skeleton>
    </Stack>
  );
};
